import { z } from "zod";
import { isSafeUploadFileName } from "@/lib/security/user-paths";
import { mediaAssetSlimSchema } from "@/validations/media";

export const CHAT_ATTACHMENT_KIND = ["image", "video", "voice"] as const;

/**
 * How long after sending a message can still be edited — WhatsApp's rule.
 * One number for the server (which enforces it) and the UI (which stops
 * offering Edit), so the menu never offers an edit the server will refuse.
 */
export const CHAT_EDIT_WINDOW_MS = 15 * 60 * 1000;

export function isWithinChatEditWindow(
  createdAt: Date | string | number,
  now: number = Date.now(),
) {
  const sentAt = new Date(createdAt).getTime();
  return Number.isFinite(sentAt) && now - sentAt < CHAT_EDIT_WINDOW_MS;
}
export type ChatAttachmentKind = (typeof CHAT_ATTACHMENT_KIND)[number];
export const CHAT_UPLOAD_SLOT = [
  "message-image",
  "message-video",
  "voice-note",
  "group-avatar",
] as const;
export type ChatUploadSlot = (typeof CHAT_UPLOAD_SLOT)[number];

export const CHAT_UPLOAD_LIMITS = {
  "message-image": {
    maxBytes: 12 * 1024 * 1024,
    mimeTypes: [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/heic",
      "image/heif",
      "image/avif",
      "image/gif",
    ],
  },
  "message-video": {
    maxBytes: 128 * 1024 * 1024,
    mimeTypes: [
      "video/mp4",
      "video/quicktime",
      "video/webm",
      "video/ogg",
      "video/mpeg",
    ],
  },
  "group-avatar": {
    maxBytes: 8 * 1024 * 1024,
    mimeTypes: [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/heic",
      "image/heif",
      "image/avif",
    ],
  },
  "voice-note": {
    maxBytes: 25 * 1024 * 1024,
    mimeTypes: [
      "audio/mpeg",
      "audio/mp3",
      "audio/mp4",
      "audio/m4a",
      "audio/aac",
      "audio/wav",
      "audio/wave",
      "audio/x-wav",
      "audio/ogg",
      "audio/webm",
    ],
  },
} as const;

export const chatMessageAttachmentSchema = z
  .object({
    kind: z.enum(CHAT_ATTACHMENT_KIND),
    asset: mediaAssetSlimSchema,
    durationMs: z.number().int().min(0).max(120000).optional(),
    waveform: z.array(z.number().int().min(0).max(100)).max(256).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.kind === "voice" && value.durationMs == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["durationMs"],
        message: "Voice notes must include duration",
      });
    }

    if (value.kind !== "voice" && value.waveform != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["waveform"],
        message: "Waveform is only supported for voice notes",
      });
    }
  });

/**
 * A link preview the server fetched for the composer, carried back on send.
 * `signature` is the server's HMAC over `requestedUrl` + `preview`, so a
 * client can't invent a title or image for someone else's link.
 */
export const signedLinkPreviewSchema = z.object({
  requestedUrl: z.string().url().max(2048),
  preview: z.object({
    url: z.string().url().max(2048),
    title: z.string().max(200).nullable().optional(),
    description: z.string().max(300).nullable().optional(),
    image: z.string().url().max(2048).nullable().optional(),
    siteName: z.string().max(200).nullable().optional(),
  }),
  signature: z.string().regex(/^[a-f0-9]{64}$/),
});

export type SignedLinkPreview = z.infer<typeof signedLinkPreviewSchema>;

export const chatMessageSchema = z
  .object({
    content: z.string().max(2000, "Message is too long"),
    attachments: z.array(chatMessageAttachmentSchema).max(10).optional(),
  })
  .superRefine((value, ctx) => {
    const hasContent = value.content.trim().length > 0;
    const hasAttachments = (value.attachments?.length ?? 0) > 0;
    if (!hasContent && !hasAttachments) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["content"],
        message: "Message cannot be empty",
      });
    }
  });

export const createGroupChatSchema = z.object({
  name: z.string().trim().min(1).max(80),
  avatar: mediaAssetSlimSchema.optional(),
  participantUserIds: z
    .array(z.string().min(1))
    .min(1)
    .max(255)
    .superRefine((ids, ctx) => {
      const deduped = new Set(ids);
      if (deduped.size !== ids.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Duplicate participants are not allowed",
        });
      }
    }),
});

export const chatLifecycleSchema = z.object({
  chatId: z.number().int().positive(),
});

export const chatTypingSchema = z.object({
  chatId: z.number().int().positive(),
  isTyping: z.boolean(),
});

export const chatMembersMutationSchema = z.object({
  chatId: z.number().int().positive(),
  userIds: z.array(z.string().min(1)).min(1).max(255),
});

export const chatRoleMutationSchema = z.object({
  chatId: z.number().int().positive(),
  userId: z.string().min(1),
});

export const markChatSeenSchema = z.object({
  chatId: z.number().int().positive(),
  messageId: z.number().int().positive(),
});

export const chatDeltaSchema = z.object({
  chatId: z.number().int().positive(),
  afterCreatedAt: z.coerce.date().optional(),
  afterMessageId: z.number().int().positive().optional(),
  limit: z.number().int().min(1).max(200).default(100),
});

export const chatUploadRequestSchema = z
  .object({
    chatId: z.number().int().positive(),
    slot: z.enum(CHAT_UPLOAD_SLOT),
    fileName: z.string().min(1).max(255).refine(isSafeUploadFileName),
    mimeType: z.string().min(1).max(128),
    bytes: z.number().int().positive(),
  })
  .superRefine((value, ctx) => {
    const slotRules = CHAT_UPLOAD_LIMITS[value.slot];
    const mimeType = value.mimeType.toLowerCase();
    const mimeAllowed = slotRules.mimeTypes.some(
      (allowedMime) => allowedMime === mimeType,
    );
    if (!mimeAllowed) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["mimeType"],
        message: `Unsupported file type for ${value.slot}`,
      });
    }

    if (value.bytes > slotRules.maxBytes) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["bytes"],
        message: `File exceeds ${Math.floor(slotRules.maxBytes / (1024 * 1024))}MB limit for ${value.slot}`,
      });
    }
  });

export type ChatMessageData = z.infer<typeof chatMessageSchema>;
